import * as React from 'react';
import { View } from 'react-native';
import { useStyles } from 'react-native-unistyles';

import { formatNumber, type TxKeyPath } from '@/core';
import { Button, Card, Checkbox, Input, Text } from '@/ui';

import { Icon } from '../icon';
import { Checkmark } from '../icons/checkmark';

interface CreditsDiscountProps {
  checkboxEnabled?: boolean;
  checkboxTitle?: string;
  onCheck?: (checked: boolean) => void;
  title: string;
  inputLabel: string;
  inputValue?: string;
  buttonTx: TxKeyPath;
  credits: number;
  onPress: (credits: number) => void;
  onSuccess: () => void;
  disabled?: boolean;
  available?: boolean;
  onChangeText?: (text: string) => void;
  min?: number;
  max?: number;
  keyboardType?: string;
}

export const CreditsDiscount = ({
  checkboxEnabled = false,
  checkboxTitle,
  onCheck,
  title,
  inputLabel,
  inputValue,
  buttonTx,
  credits,
  onPress,
  disabled = false,
  available = true,
  onChangeText,
  min,
  max,
  keyboardType,
}: CreditsDiscountProps) => {
  const { theme } = useStyles();
  const [text, setText] = React.useState(inputValue);
  const [checked, setChecked] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState('');

  return (
    <View>
      <Card>
        {checkboxTitle ? (
          <View
            style={{
              ...(checked && {
                borderBottomColor: theme.colors.card.default.borderColor,
                borderBottomWidth: 1,
              }),
            }}
          >
            <View style={{ padding: 16 }}>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <Checkbox
                  onPress={() => {
                    setChecked(!checked);
                    onCheck?.(!checked);

                    if (checked) {
                      setText('');
                      setSuccess(false);
                    }
                  }}
                  active={checked}
                  disabled={!checkboxEnabled}
                />

                <Text tx="checkout.applyEonCredits" type="title2" />
              </View>
            </View>
          </View>
        ) : null}

        {checked ? (
          <View style={{ padding: 16, gap: 16 }}>
            <Text type="title2">{title}</Text>

            {available ? (
              <>
                <Input
                  keyboardType={keyboardType}
                  label={inputLabel}
                  value={text}
                  onChangeText={(t: string) => {
                    setText(t);
                    onChangeText?.(t);

                    setSuccess(false);

                    if (min && Number(t) < min) {
                      setError(`Amount needs to exceed $${formatNumber(min)}`);
                    }

                    if (max && Number(t) > max) {
                      setError(`Amount cannot exceed $${formatNumber(max)}`);
                    }

                    if (Number(t) > credits) {
                      setError('Amount cannot exceed available credits');
                    } else if (error) {
                      setError('');
                    }
                  }}
                  error={error}
                />

                <Button
                  tx={buttonTx}
                  type="primarySmall"
                  onPress={() => {
                    onPress(Number(text));

                    setSuccess(true);
                  }}
                  style={{ width: 170, padding: 0, margin: 0 }}
                  disabled={disabled || error || !text}
                />

                {success ? (
                  <View
                    style={{
                      flexDirection: 'row',
                      gap: 8,
                    }}
                  >
                    <Icon
                      type="naked"
                      icon={<Checkmark />}
                      stroke={theme.colors.input.success.helperText}
                      width={12}
                      height={12}
                    />

                    <Text
                      style={{
                        color: theme.colors.text.secondary,
                      }}
                      type="bodyRegular"
                    >
                      A ${text} credit has been added to your booking. 💰
                    </Text>
                  </View>
                ) : null}
              </>
            ) : null}
          </View>
        ) : null}
      </Card>
    </View>
  );
};
